import React, { useMemo, useEffect, useCallback, type MouseEvent, type ChangeEvent } from 'react';
import { Helmet } from 'react-helmet-async';
import { useFormik } from 'formik';
import { useParams, useNavigate } from 'react-router-dom';
import {
	Button,
	Card,
	Stack,
	LinearProgress,
	CardContent,
	TextField,
	Typography,
	InputAdornment,
	Switch,
	Grid,
} from '@mui/material';

import { Test } from '../../models';
import { useGetTestQuery, useUpdateTestMutation } from '../../slices/apis/testApi';

export default function TestEdit() {
	const { testId } = useParams();
	const navigate = useNavigate();
	const [updateTest, { isSuccess }] = useUpdateTestMutation();

	const { data: test, isLoading: isLoadingTest } = useGetTestQuery(testId);

	const formik = useFormik({
		initialValues: useMemo(() => {
			if (test) {
				return Test.schema.cast({
					...test,
				});
			}
			return Test.schema.getDefault();
		}, [test]),
		enableReinitialize: true,
		validationSchema: Test.schema,
		onSubmit: async (values) => {
			await updateTest(values);
		},
	});

	const handleNavigate = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			navigate(e.currentTarget.getAttribute('data-route') as string);
		},
		[navigate],
	);

	const handleBulkTestSwitch = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			formik.setFieldValue('isBulk', e.target.checked);
		},
		[formik],
	);

	useEffect(() => {
		if (isSuccess) {
			navigate('/dashboard/admin/tests');
		}
	}, [isSuccess, navigate]);

	if (isLoadingTest) {
		return <LinearProgress />;
	}

	return (
		<>
			<Helmet>
				<title> Edit Test - {process.env.REACT_APP_ORG} </title>
			</Helmet>
			<Grid container justifyContent="start">
				<Grid size={{ md: 6 }}>
					<Typography variant="h4" gutterBottom>
						Edit Test {testId}
					</Typography>
					<Card>
						<CardContent>
							<form onSubmit={formik.handleSubmit}>
								<Grid container spacing={2}>
									<Grid size={{ md: 12 }}>
										<TextField
											fullWidth
											margin="dense"
											id="name"
											name="name"
											label="Name"
											variant="standard"
											value={formik.values.name ?? ''}
											onChange={formik.handleChange}
											error={formik.touched.name && Boolean(formik.errors.name)}
											helperText={formik.touched.name && formik.errors.name}
										/>
									</Grid>
									<Grid size={{ md: 12 }}>
										<TextField
											fullWidth
											margin="dense"
											id="desc"
											name="desc"
											label="Description"
											variant="standard"
											value={formik.values.desc ?? ''}
											onChange={formik.handleChange}
											error={formik.touched.desc && Boolean(formik.errors.desc)}
											helperText={formik.touched.desc && formik.errors.desc}
										/>
									</Grid>
									<Grid size={{ md: 12 }}>
										<TextField
											fullWidth
											margin="dense"
											id="price"
											name="price"
											label="Price"
											variant="standard"
											type="number"
											value={formik.values.price ?? 0}
											onChange={formik.handleChange}
											error={formik.touched.price && Boolean(formik.errors.price)}
											helperText={formik.touched.price && formik.errors.price}
											slotProps={{
												input: {
													startAdornment: <InputAdornment position="start">$</InputAdornment>,
												},
											}}
										/>
									</Grid>

									<Grid size={{ md: 12 }}>
										<Stack alignItems="flex-start" spacing={0.5}>
											<Typography variant="subtitle2" color="grey.500">
												Allow Bulk Request
											</Typography>
											<Switch checked={Boolean(formik.values.isBulk)} onChange={handleBulkTestSwitch} />
										</Stack>
									</Grid>

									<Grid size={{ md: 4 }}>
										<Grid container justifyContent="start" spacing={4}>
											<Grid size={{ md: 6 }}>
												<Button loading={formik.isSubmitting} variant="outlined" color="primary" type="submit">
													<span>Update</span>
												</Button>
											</Grid>
											<Grid size={{ md: 6 }}>
												<Button
													color="error"
													variant="outlined"
													data-route="/dashboard/admin/tests"
													onClick={handleNavigate}
												>
													Cancel
												</Button>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</form>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</>
	);
}
